import React, { useEffect, useState } from 'react';
import { AppBar, Box, Button, Grid, Typography } from '@material-ui/core';
import VerticalTabsMenu from '../ProfilesTabs/VerticalTabsMenu';
import { TabPanel } from '../ProfilesTabs/TabPanel';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import useStyles from '../ProfilesTabs/styles';
import { AvailabilityTemperature } from './AvailabilityTemperature';
import { clearHoldingProfile } from '../../redux/actions/equipmentsActions';
import {
  createEntity as createHoldingProfile,
  updateEntity as updateHoldingProfile,
} from '../../redux/actions/equipmentHoldingProfileActions';
import {
  availabilityToFormSerializer,
  formtoTemperaturesSerializer,
  transformAvailabilityToPayload,
  VIEW_MODE_STATES,
} from './utils';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { InitializeTemperature } from '../ProfilesTabs/helpers/InitializeTemperature';
import { selectModelsName } from '../../redux/selectors/modelsSelector';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';
import { useTranslation } from 'react-i18next';
import { isDisabledSaveButtonEdit } from '../ProfilesTabs/helpers/isDisabledSaveButton';
import { AppDispatch } from '../../redux/store';

const EquipmentHoldingProfileForm = props => {
  const dispatch: any = useDispatch<AppDispatch>();
  const classes = useStyles();
  const {
    setViewMode,
    viewMode,
    searchFilters = {},
    handleCancel,
    onClose,
    setCopyMessage,
    autocompleteSelectors,
  } = props;
  const [tabValue, setTabValue] = React.useState(0);
  const { selectedBrand, countries, states } = useSelector(
    (state: RootStateOrAny) => ({
      manufacturersCatalog: state.ManufacturersReducer.entities,
      categoriesCatalog: state.CategoriesReducer.entities,
      modelsCatalog: selectModelsName(state),
      regions: state.RegionsReducer.regions,
      countries: state.RegionsReducer.countries,
      states: state.RegionsReducer.states,
      selectedBrand: state.UIReducer.selectedBrand,
    }),
  );
  const equipmentHoldingProfile = useSelector(
    (state: RootStateOrAny) => state.EquipmentsReducer.equipmentHoldingProfile,
  );
  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const [availabilities, setAvailabilities] = useState(
    [].map(availabilityToFormSerializer),
  );
  const [errorValidations, setErrors] = useState({});

  useEffect(() => {
    if (equipmentHoldingProfile?.availabilities?.length) {
      setAvailabilities(
        sortAvailabilities(
          equipmentHoldingProfile.availabilities.map(
            availabilityToFormSerializer,
          ),
        ),
      );
    }
  }, [equipmentHoldingProfile]);

  function sortAvailabilities(array) {
    if (!(array && Array.isArray(array))) return array;

    let regions: any[] = [];
    let countries: any[] = [];
    let states: any[] = [];
    array.forEach(profile => {
      if (!profile?.country?.description && !regions.includes(profile))
        regions.push(profile);
      else if (!profile?.state?.description && !countries.includes(profile))
        countries.push(profile);
      else if (!states.includes(profile)) states.push(profile);
    });

    regions = regions.sort((regionA, regionB) => {
      if (regionA === 'GLOBAL') return -1;
      if (regionB === 'GLOBAL') return 1;
      return regionA?.region > regionB?.region
        ? 1
        : regionA?.region < regionB?.region
          ? -1
          : 0;
    });
    countries = countries.sort((A, B) =>
      A?.country.description > B?.country.description
        ? 1
        : A?.country.description < B?.country.description
          ? -1
          : 0,
    );

    states = states.sort((A, B) =>
      A?.state.description > B?.state.description
        ? 1
        : A?.state.description < B?.state.description
          ? -1
          : 0,
    );

    return [...regions, ...countries, ...states];
  }

  const isMissingRequired = () => {
    let isEmptyField = false;
    if (!availabilities.length) {
      return false;
    }

    availabilities.forEach(item => {
      if (!item.holdingMaxF || !item.holdingMinF) {
        isEmptyField = true;
      }
    });

    return isEmptyField;
  };
  const handleAddProfile = () => {
    const newAvailability = availabilityToFormSerializer({
      region: searchFilters.region,
      country:
        countries.find(
          countryElement => searchFilters.country === countryElement.id,
        ) || null,
      state:
        states.find(stateElement => searchFilters.state === stateElement.id) ||
        null,
      country_id:
        searchFilters.country === 'all' ? null : searchFilters.country,
      state_id: searchFilters.state === 'all' ? null : searchFilters.state,
      ...InitializeTemperature(),
    });
    const isNew = !availabilities.find(
      av =>
        av.region == newAvailability.region &&
        av.country_id == newAvailability.country_id &&
        av.state_id == newAvailability.state_id,
    );
    if (isNew) {
      setAvailabilities(prevState => {
        newAvailability.isNew = true;
        const sortedArray = sortAvailabilities([newAvailability, ...prevState]);
        setTabValue(-1);
        return sortedArray;
      });
    } else {
      dispatch(snackbarShowMessage('app.common.messages.profileAlreadyExist'));
    }
  };

  useEffect(() => {
    if (availabilities && availabilities.length > 0) {
      const idx = availabilities.findIndex(a => a.isNew);
      if (idx > -1) {
        setTabValue(idx);
        delete availabilities[idx].isNew;
        setAvailabilities(availabilities);
      }
    }
  }, [tabValue]);

  const handleBackButtonClick = () => {
    setCopyMessage(false);
    handleCancel();
    setErrors({});
    setViewMode(VIEW_MODE_STATES.LIST);
    dispatch(clearHoldingProfile());
  };
  const removeAvailability = index => () => {
    setAvailabilities(state => {
      state.splice(index, 1);
      return sortAvailabilities([...state]);
    });
    setTabValue(0);
  };

  const handleSubmit = () => {
    setCopyMessage(false);
    const payload = { ...equipmentHoldingProfile };
    payload.availabilities = availabilities.map(transformAvailabilityToPayload);

    if (viewMode === VIEW_MODE_STATES.EDIT) {
      dispatch(
        updateHoldingProfile(
          { availabilities: payload.availabilities },
          equipmentHoldingProfile.id,
        ),
      ).then(() => {
        onClose();
        dispatch(snackbarShowMessage('equipment.holdingProfile.updateSuccess'));
        dispatch(clearHoldingProfile());
        setViewMode(VIEW_MODE_STATES.LIST);
      });
      return;
    }
    payload.brand = selectedBrand;
    payload.manufacturer_id = autocompleteSelectors.manufacturer.id;
    payload.category_id = autocompleteSelectors.category.id;
    payload.model = autocompleteSelectors.model.id;
    dispatch(createHoldingProfile(payload)).then(() => {
      onClose();
      dispatch(clearHoldingProfile());
      dispatch(snackbarShowMessage('equipment.holdingProfile.createSuccess'));
      setViewMode(VIEW_MODE_STATES.LIST);
    });
  };

  const updateAvailabilityTemperature = (availabilityTemperatures, index) => {
    setAvailabilities(state => {
      state[index]['formTemperatures'] = availabilityTemperatures;
      state[index] = formtoTemperaturesSerializer(state[index]);
      return sortAvailabilities([...state]);
    });
  };

  const { t } = useTranslation();

  return (
    <>
      <div>
        <AppBar className={classes.holdingProfilesContainer}>
          <div className={classes.containerAppBar}>
            <Typography className={classes.containerTitle}>
              {t('equipment.holdingProfiles')}
            </Typography>
            {
              <div className={classes.addMore}>
                {thisUser.hasHoldingProfileEquipment && (
                  <Button
                    onClick={handleAddProfile}
                    disabled={isMissingRequired()}
                    style={{
                      color: 'white',
                      textTransform: 'none',
                    }}
                    endIcon={<AddIcon />}
                  >
                    {t('AddNewAvailability')}
                  </Button>
                )}
              </div>
            }
          </div>
        </AppBar>
      </div>

      <form className={classes.root}>
        <VerticalTabsMenu
          hideDefault={true}
          profiles={availabilities}
          value={tabValue}
          setValue={setTabValue}
        />
        {availabilities.map((availability, index) => (
          <TabPanel value={tabValue} index={index}>
            <Grid container>
              <Box ml={'auto'}>
                <Button
                  variant="outlined"
                  disabled={availabilities.length <= 1}
                  size="small"
                  endIcon={<DeleteIcon />}
                  onClick={removeAvailability(index)}
                >
                  {t('app.common.delete')}
                </Button>
              </Box>

              <AvailabilityTemperature
                index={index}
                availability={availability}
                handleAvailabilityChange={updateAvailabilityTemperature}
                errorValidations={errorValidations}
                setErrors={setErrors}
              />
            </Grid>
          </TabPanel>
        ))}
      </form>
      <Grid container>
        <Grid item>
          <Box m={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBackButtonClick}
            >
              {t('equipment.holdingProfileBackToList')}
            </Button>
          </Box>
        </Grid>
        <Grid item>
          <Box m={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={
                isMissingRequired() ||
                !availabilities.length ||
                isDisabledSaveButtonEdit(
                  availabilities.map(a => ({
                    temperatures: a.formTemperatures,
                    isRequired: true,
                  })),
                  errorValidations,
                )
              }
            >
              {t('app.common.save')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default EquipmentHoldingProfileForm;
